import {
	ICustomerLanguageInfo,
	IMenuTranslation,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	DirectionForPaginated,
} from 'types';

// Menu v1
export interface IGetPaginatedModifierGroups {
	pageNumber: number;
	pageSize: number;
	searchByName: Nullable<string>;
	customerId: Nullable<string>;
	restaurantId: Nullable<string>;
	sortByName: Nullable<string>;
}
// Menu v1
// Menu v2
export enum SortByForPaginatedModifierGroups {
	Name = 'Name',
}

export interface IGetPaginatedModifierGroupsV2 {
	customerId: string;
	restaurantId: Nullable<string>;
	pageNumber: number;
	pageSize: number;
	searchInput: Nullable<string>;
	sorting: Nullable<{
		sortBy: SortByForPaginatedModifierGroups;
		direction: DirectionForPaginated;
	}>;
}
// Menu v2

export interface IRestaurantsModifierGroupsForModifierGroups {
	id: string;
	restaurantId: string;
	restaurantName: Nullable<string>;
}

export interface IModifiersForModifierGroups {
	id: string;
	modifierId: string;
	modifierName: Nullable<string>;
	positionNumber: number;
}

export interface IDishesForModifierGroups {
	id: string;
	dishName: Nullable<string>;
	modifierGroupId: string;
	positionNumber: number;
	externalId: Nullable<string>;
}

export interface IItemModifierGroups {
	id: string;
	name: Nullable<string>;
	articleNumber: Nullable<string>;
	customerId: string;
	customerName: Nullable<string>;
	restaurantsModifierGroups: IRestaurantsModifierGroupsForModifierGroups[];
}

// Menu v1
export interface IModifierGroups {
	modifierGroups: IItemModifierGroups[];
	modifierGroupsCount: number;
}
// Menu v1
// Menu v2
export interface IModifierGroupsV2 {
	items: IModifierGroupV2Item[];
	totalCount: number;
}
// Menu v2

interface IRestaurantsModifierGroupsForModifierGroupsInfo {
	id: string;
	restaurantName: Nullable<string>;
	restaurantId: string;
	forceDisable: boolean;
}

export interface IModifiersForModifierGroupsInfo {
	id: string;
	modifierId: string;
	modifierName: Nullable<string>;
	positionNumber: number;
}

interface IDishesForModifierGroupsInfo {
	id: string;
	dishName: Nullable<string>;
	modifierGroupId: string;
	positionNumber: number;
	categoryIds: string[];
	subCategoryIds: string[];
}

export interface IModifierGroupsInfo {
	id: string;
	name: Nullable<string>;
	externalId: Nullable<string>;
	articleNumber: Nullable<string>;
	customerId: string;
	customerName: Nullable<string>;
	restaurantsModifierGroups: IRestaurantsModifierGroupsForModifierGroupsInfo[];
	modifiers: IModifiersForModifierGroupsInfo[];
	dishes: IDishesForModifierGroupsInfo[];
	customer: ICustomerLanguageInfo | null;
	translations: IMenuTranslation[];
}

// Menu v2
export interface IGetModifierGroupInfoV2 {
	id: string;
	customer: IItemForGetShortInfo;
	name: string;
	isCreatedOnPos: boolean;
	externalId: string;
	articleNumber: string;
	translations: IMenuTranslation[];
	restaurants: IItemForGetShortInfoWithStatus[];
}
// Menu v2
export interface IGetModifierGroupsInfo {
	data: {
		modifierGroup: IModifierGroupsInfo;
	};
}

export interface IItemModifierGroupsPaginatedInfo extends IItemModifierGroups {
	externalId: Nullable<string>;
	modifiers: IModifiersForModifierGroups[];
	dishes: IDishesForModifierGroups[];
}

export interface IGetModifierGroupPaginatedInfo {
	data: IItemModifierGroupsPaginatedInfo;
}

export interface IGetModifierGroupsShort {
	restaurantIds: string[];
}

export interface IGetModifierGroupsShortResponse {
	data: {
		modifierGroups: IItemForGetShortInfo[];
	};
}

// Menu v1
export interface ICreateModifierGroups {
	name: string;
	customerId: string;
	restaurantIds: string[];
	modifierIds: string[];
	dishIds: string[];
	translations: IMenuTranslation[];
}
// Menu v1
// Menu v2
export interface ICreateModifierGroupsV2 {
	name: string;
	externalId: string;
	customerId: string;
	restaurantIds: string[];
	translations: IMenuTranslation[];
}
// Menu v2

export interface IUpdateModifierGroups {
	id: string;
	name: string;
	externalId: string | null;
	customerId: string;
	restaurants: {
		restaurantId: string;
		forceDisable: boolean;
	}[];
	dishIds: string[];
	translations: IMenuTranslation[];
}

export interface IUpdateModifierGroupsV2 {
	id: string;
	name: string;
	articleNumber: string;
	externalId: string;
	translations: {
		languageId: string;
		name: string;
	}[];
	restaurantIds: string[];
}

export type TModGroupProductType = 'Dish' | 'Modifier';

export interface IGetModifierGroupsTemplateInfoArgs {
	productId: string;
	type: TModGroupProductType;
}

export interface IGetModifierGroupsTemplateInfo {
	customer: IItemForGetShortInfo;
	product: {
		id: string;
		name: Nullable<string>;
		externalId: Nullable<string>;
	};
	templates: {
		id: string;
		name: Nullable<string>;
		upLimit: Nullable<number>;
		downLimit: Nullable<number>;
		isLimitsProtected: boolean;
		isAdditional: boolean;
	}[];
}

export interface IUpdateModifierGroupsTemplateInfo {
	productId: string;
	type: TModGroupProductType;
	modifierGroups: {
		id: string;
		upLimit: Nullable<number>;
		downLimit: Nullable<number>;
		isLimitsProtected: boolean;
		isAdditional: boolean;
	}[];
}

export interface IModifierGroupV2Item {
	id: string;
	name: string;
	articleNumber: string;
	externalId: string;
	restaurants: IItemForGetShortInfo[];
}

export enum RestaurantModGroupPropertiesType {
	Dish = 'Dish',
	Modifier = 'Modifier',
	DishAndModifier = 'DishAndModifier',
}

export interface IModGroupParentItem {
	id: string;
	name: string;
	externalId: string;
	type: RestaurantModGroupPropertiesType;
	inclusive: number;
	isPosPortion: boolean;
}

export interface IModGroupChildItem {
	id: string;
	name: string;
	externalId: string;
	isDefault: boolean;
	type: RestaurantModGroupPropertiesType;
	isFromTemplate: boolean;
}

export interface IModGroupModifierItem {
	modifierId: string;
	modifierName: string;
	modifierType: RestaurantModGroupPropertiesType;
	parentId: string;
	parentName: string;
	portionId: string;
	portionName: string;
}

export interface IGetModGroupRestaurantProperties {
	customer: IItemForGetShortInfo;
	restaurantId: string;
	id: string;
	name: string;
	externalId: string;
	forceDisable: boolean;
	isPortionFlow: boolean;
	parents: IModGroupParentItem[];
	children: IModGroupChildItem[];
	parentChildRelations: IModGroupModifierItem[];
}

export interface IGetModGroupRestaurantPropertiesArguments {
	restaurantId: string;
	modGroupId: string;
}

export interface IModifierTemplateItem {
	id: string;
	externalId: string;
	name: string;
}

export interface IGetModifierTemplatesResponse {
	modifiers: IModifierTemplateItem[];
}

export interface IGetAvailableModifiersResponse {
	products: IItemForGetShortInfo[];
}

export interface IRestaurantPropertiesModifierGroup {
	id: string;
	name: string;
	upLimit: Nullable<number>;
	downLimit: Nullable<number>;
	inclusive: Nullable<number>;
	isLimitsProtected: Nullable<boolean>;
	isAdditional: Nullable<boolean>;
	isTemplate: boolean;
}

export interface IGetModifierGroupsByProductIdAndRestaurantIdArguments {
	productId: string;
	restaurantId: string;
	type: TModGroupProductType;
}

export interface IGetModifierGroupsByProductIdAndRestaurantId {
	data: {
		customer: IItemForGetShortInfo;
		restaurantId: string;
		product: {
			id: string;
			name: string;
			externalId: string;
		};
		modifierGroups: IRestaurantPropertiesModifierGroup[];
	};
}

export interface IGetAllModifierGroupsFromCustomerByProductIdAndRestaurantId {
	data: {
		modifierGroups: IRestaurantPropertiesModifierGroup[];
	};
}

export interface IGetModifiersForAssignArguments {
	modGroupId: string;
	restaurantId: string;
}

export interface IModifierForAssign {
	id: string;
	name: string;
	isFromTemplate: boolean;
	isDefault: boolean;
}

export interface IGetModifiersForAssignResponse {
	data: {
		modifiers: IModifierForAssign[];
	};
}

export interface IUpdateModGroupRestaurantProperties {
	restaurantId: string;
	modifierGroupId: string;
	forceDisable: boolean;
	parentIds: string[];
	childIds: string[];
}

export type TPropertiesOfPatchChangesForModGroup = 'forceDisable';

export interface IPropertiesOfPatchChangesForModGroup {
	id: TPropertiesOfPatchChangesForModGroup;
	name: TPropertiesOfPatchChangesForModGroup;
}

export interface IUpdateRestaurantPropertiesAsPatchForModGroup {
	modifierGroupId: string;
	restaurantIds: string[];
	update: {
		forceDisable: {
			shouldUpdate: boolean;
			value: boolean;
		};
		parents: {
			shouldUpdate: boolean;
			value: string[];
		};
		children: {
			shouldUpdate: boolean;
			value: string[];
		};
	};
}

export interface IUpdateModGroupProductsInclusive {
	restaurantId: string;
	modifierGroupId: string;
	products: {
		productId: string;
		inclusive: number;
	}[];
}
